import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, TextField, Stack, Typography, Chip, IconButton, Tooltip,
  ToggleButton, ToggleButtonGroup, Popover, Divider, Alert, Link, Paper,
  Checkbox, ButtonBase, Button,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as richText from '../../utils/richText';

/*
 * Description editor.
 *
 * There is exactly one piece of data — the stored text and its format (`value`
 * and `format`). Everything else is a lens over it:
 *
 *   CHECKBOX    — declares what the stored text IS. Relabels the base; never
 *                 alters a character. This is the only control that changes
 *                 what gets saved.
 *   FORMAT TABS — convert the stored text for display so you can see what the
 *                 API will return for that format. Pure view: switching tabs
 *                 does not touch the stored value, so it can never dirty the
 *                 form or change what is persisted.
 *   VIEW        — Raw or Preview. Purely a display choice.
 *
 * Why the lens matters: the platform doesn't render descriptions to end
 * customers, it hands them to third-party systems via the API. A tenant must be
 * able to inspect "what would this look like as RTF?" without that inspection
 * silently rewriting their description. An earlier version converted the
 * working text on every tab switch, and because conversions are lossy the
 * round trip registered as an edit — enabling Save on a page the user had only
 * looked at.
 *
 * Typing while a non-base tab is shown still works: the edit is converted back
 * into the stored format, so the ticked format stays authoritative.
 *
 * Preview renders the AST to React elements rather than injecting HTML, so
 * there is no dangerouslySetInnerHTML anywhere in either portal and stored
 * markup can never execute.
 *
 * Conversion runs the same code as the API (see scripts/sync-rich-text.js).
 */

const FORMAT_LABELS = {
  plain: 'Plain text',
  markdown: 'Markdown',
  html: 'HTML',
  rtf: 'RTF',
};

const FORMAT_HINTS = {
  plain: 'No formatting. Blank lines separate paragraphs.',
  markdown: '**bold**, *italic*, # heading, - list, [link](url)',
  html: '<strong>, <em>, <u>, <h1>-<h6>, <ul>, <a href>. Other tags and all attributes are stripped.',
  rtf: 'Rich Text Format for word processors.',
};

// ─── Preview renderer (AST -> React elements, never innerHTML) ────────────────

function renderInline(nodes, prefix) {
  return (nodes || []).map((n, i) => {
    const key = `${prefix}-${i}`;
    switch (n.type) {
      case 'text': return <React.Fragment key={key}>{n.value}</React.Fragment>;
      case 'break': return <br key={key} />;
      case 'strong': return <strong key={key}>{renderInline(n.children, key)}</strong>;
      case 'em': return <em key={key}>{renderInline(n.children, key)}</em>;
      case 'underline': return <u key={key}>{renderInline(n.children, key)}</u>;
      case 'strike': return <s key={key}>{renderInline(n.children, key)}</s>;
      case 'code':
        return (
          <Box key={key} component="code" sx={{
            fontFamily: 'monospace', fontSize: '0.875em',
            bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5,
          }}>{n.value}</Box>
        );
      case 'link':
        // href was scheme-validated during parsing; unsafe URLs never become
        // link nodes in the first place.
        return (
          <Link key={key} href={n.href} target="_blank" rel="noopener noreferrer">
            {renderInline(n.children, key)}
          </Link>
        );
      default: return null;
    }
  });
}

function renderBlocks(blocks, prefix = 'b') {
  return (blocks || []).map((b, i) => {
    const key = `${prefix}-${i}`;
    switch (b.type) {
      case 'heading':
        return (
          <Typography
            key={key}
            variant={`h${Math.min(b.level + 2, 6)}`}
            sx={{ mt: i === 0 ? 0 : 1.5, mb: 0.5, fontWeight: 700 }}
          >
            {renderInline(b.children, key)}
          </Typography>
        );
      case 'thematicBreak': return <Divider key={key} sx={{ my: 1.5 }} />;
      case 'codeBlock':
        return (
          <Box key={key} component="pre" sx={{
            fontFamily: 'monospace', fontSize: 13, bgcolor: 'action.hover',
            p: 1, borderRadius: 1, overflowX: 'auto', my: 1,
          }}>{b.value}</Box>
        );
      case 'blockquote':
        return (
          <Box key={key} sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5, my: 1, color: 'text.secondary' }}>
            {renderBlocks(b.blocks, key)}
          </Box>
        );
      case 'list':
        return (
          <Box key={key} component={b.ordered ? 'ol' : 'ul'} sx={{ pl: 3, my: 1 }}>
            {b.items.map((item, j) => (
              <Box component="li" key={`${key}-${j}`} sx={{ mb: 0.25 }}>
                {renderBlocks(item, `${key}-${j}`)}
              </Box>
            ))}
          </Box>
        );
      default:
        return (
          <Typography key={key} variant="body2" sx={{ my: 0.75 }}>
            {renderInline(b.children, key)}
          </Typography>
        );
    }
  });
}

/** The (!) popover: format caveats live here, not cluttering the form. */
function FormatInfo({ currentFormat }) {
  const [anchor, setAnchor] = useState(null);
  const notes = useMemo(() => {
    const relevant = richText.LIMITATIONS.filter(
      (l) => l.from === currentFormat || l.from === 'any' || l.to === currentFormat
    );
    return relevant.length > 0 ? relevant : richText.LIMITATIONS;
  }, [currentFormat]);

  return (
    <>
      <Tooltip title="Formatting support and conversion limits">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label="Formatting information">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 460, p: 2 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          What carries across formats
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {richText.SUPPORTED.map((s) => <Chip key={s} label={s} size="small" variant="outlined" />)}
        </Box>
        <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
          Anything outside that list is dropped when converting between formats.
          Changing the view never changes your text — only the <strong>Save as</strong> buttons do.
        </Alert>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Known limitations</Typography>
        <Stack spacing={1}>
          {notes.map((l, i) => (
            <Box key={i}>
              <Chip
                size="small" color="warning" variant="outlined" sx={{ mb: 0.5 }}
                label={l.from === 'any' && l.to === 'any'
                  ? 'All formats'
                  : `${FORMAT_LABELS[l.from] || l.from} → ${l.to === 'any' ? 'any format' : (FORMAT_LABELS[l.to] || l.to)}`}
              />
              <Typography variant="caption" color="text.secondary" display="block">{l.note}</Typography>
            </Box>
          ))}
        </Stack>
      </Popover>
    </>
  );
}

export default function DescriptionField({
  value, format, onChange, disabled, maxLength, original,
}) {
  const [view, setView] = useState('raw');
  const [error, setError] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);

  // `value`/`format` are the STORED description and its format — the only
  // things that get saved. `viewFormat`/`viewText` are a lens over them.
  // Switching tabs changes only the lens, so it can never mark the form dirty
  // or alter what is persisted. Editing writes back through the lens.
  const [viewFormat, setViewFormat] = useState(format);
  const [viewText, setViewText] = useState(value || '');
  // Tracks the last value this component pushed up, so an echo of our own
  // change isn't mistaken for the parent loading a different item.
  const pushedRef = useRef(value);

  // Resync when the stored text is replaced from outside (a different item).
  // Every control inside this component resets the lens itself, so this only
  // has to cover genuinely external changes.
  useEffect(() => {
    if (value === pushedRef.current) { return; }
    pushedRef.current = value;
    setViewFormat(format);
    setViewText(value || '');
  }, [value, format]);

  // Detect against the STORED text and its declared format — the suggestion is
  // "your source looks mislabelled", which says nothing about the tab you are
  // viewing. Running this on the converted view made it fire constantly: HTML
  // viewed as Markdown still contains <u> tags, so it detected HTML and offered
  // to switch to the format already ticked.
  const detected = useMemo(
    () => (value && value.trim() ? richText.detectFormat(value) : null),
    [value]
  );
  const mismatch = Boolean(detected && detected !== format && (value || '').trim() !== '');

  const previewBlocks = useMemo(() => {
    if (view !== 'preview' || !viewText || !viewText.trim()) { return []; }
    try { return richText.parse(viewText, viewFormat); } catch { return []; }
  }, [view, viewText, viewFormat]);

  const isDirty = Boolean(
    original && ((value || '') !== (original.text || '') || format !== (original.format || 'plain'))
  );
  const isLens = viewFormat !== format;

  /** Push a new stored value up, remembering it so the sync effect ignores it. */
  const push = (text, fmt) => {
    pushedRef.current = text;
    onChange(text, fmt);
  };

  /**
   * Tick a format: declare that the STORED text is that format.
   *
   * Deliberately reinterprets `value`, not `viewText`. Viewing another tab
   * converts for display — e.g. plain -> HTML escapes the markup into entities
   * — and adopting that view on tick would store the escaped copy and destroy
   * the original. Reinterpreting the stored text keeps the promise that ticking
   * never alters a character, from any tab, so a wrong tick is free to undo.
   *
   * To actually transform the text, use the explicit convert action below.
   */
  const setBaseFormat = (next) => {
    setError(null);
    setViewFormat(next);
    setViewText(value || '');
    push(value || '', next);
  };

  /**
   * Convert the stored text into the format currently being viewed, and store
   * that. The destructive counterpart to ticking, so it is a separate,
   * explicitly labelled action rather than a side effect.
   */
  const convertStoredTo = (next) => {
    setError(null);
    setViewFormat(next);
    push(viewText, next);
  };

  /**
   * Restore the description as it was loaded, and reset the lens with it.
   *
   * Resetting explicitly matters: reverting after only ticking leaves the text
   * identical, so the resync effect sees no change and the tab and editor would
   * otherwise keep showing the previous format's converted text.
   */
  const revertToOriginal = () => {
    const text = original?.text || '';
    const fmt = original?.format || 'plain';
    setError(null);
    setViewFormat(fmt);
    setViewText(text);
    push(text, fmt);
  };

  /**
   * Switch the lens. Converts for display only — the stored value and format
   * are untouched, so browsing formats never dirties the form.
   */
  const switchFormat = (next) => {
    if (!next || next === viewFormat) { return; }
    setError(null);
    if (!value || !value.trim()) { setViewFormat(next); setViewText(''); return; }
    try {
      setViewText(richText.convert(value, format, next));
      setViewFormat(next);
    } catch (e) {
      setError(`Could not show as ${FORMAT_LABELS[next]}: ${e.message}`);
    }
  };

  /**
   * Typing. When the lens matches the stored format the text is stored as-is;
   * otherwise it is converted back so the stored format stays authoritative.
   */
  const handleEdit = (text) => {
    setViewText(text);
    if (!isLens) { push(text, format); return; }
    try { push(richText.convert(text, viewFormat, format), format); } catch { push(text, format); }
  };

  const tooLong = maxLength && (value || '').length > maxLength;
  const groupSx = { '& .MuiToggleButton-root': { py: 0.25, px: 1.25, textTransform: 'none', fontSize: 13 } };

  return (
    <Box>
      {/* Header — states what is stored, in past or future tense as appropriate. */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>Description</Typography>
        <Typography variant="caption" color="text.secondary">
          {isDirty ? 'save as' : 'saved as'}
        </Typography>
        <Chip
          label={FORMAT_LABELS[format]}
          color={isDirty ? 'warning' : 'primary'}
          size="small"
          sx={{ fontWeight: 700, fontSize: 13, height: 26 }}
        />
        <FormatInfo currentFormat={format} />
        <Box sx={{ flex: 1 }} />
        {mismatch && !disabled && (
          <Tooltip title={`Treat this text as ${FORMAT_LABELS[detected]}. The text is not changed — only how it is read.`}>
            <Chip
              size="small" color="info" variant="outlined" icon={<AutoAwesomeIcon />}
              label={`Looks like ${FORMAT_LABELS[detected]} — switch?`}
              onClick={() => setBaseFormat(detected)}
            />
          </Tooltip>
        )}
        {maxLength && (
          <Typography variant="caption" color={tooLong ? 'error' : 'text.secondary'}>
            {(value || '').length.toLocaleString()} / {maxLength.toLocaleString()}
          </Typography>
        )}
      </Stack>

      {/* Format tabs (view/edit) with an explicit "store this one" checkbox,
          then the raw/preview switch. Checking a box never alters the text. */}
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {/* The checkbox and the tab are SIBLINGS, not nested.
            Previously the checkbox lived inside a ToggleButton, which is
            invalid HTML (interactive content inside a <button>) and meant a
            single click could fire both handlers — the tab's convert then
            overwrote the tick's reinterpret, so ticking appeared to do
            nothing. Two independent controls remove that race entirely. */}
        <Box sx={{
          display: 'inline-flex', alignItems: 'stretch',
          border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
        }}>
          {richText.FORMATS.map((f, i) => {
            const isSaved = format === f;
            const isShown = viewFormat === f;
            return (
              <Box
                key={f}
                sx={{
                  display: 'flex', alignItems: 'center', pl: 0.25, pr: 0.25,
                  borderLeft: i > 0 ? 1 : 0, borderColor: 'divider',
                  bgcolor: isShown ? 'action.selected' : 'transparent',
                }}
              >
                <Tooltip title={isSaved
                  ? `This text is ${FORMAT_LABELS[f]}, and is stored as ${FORMAT_LABELS[f]}`
                  : `Treat this text as ${FORMAT_LABELS[f]} and store it as ${FORMAT_LABELS[f]}. The text itself is not changed.`}>
                  <span>
                    <Checkbox
                      size="small"
                      checked={isSaved}
                      disabled={disabled}
                      onChange={() => setBaseFormat(f)}
                      inputProps={{ 'aria-label': `Treat and store description as ${FORMAT_LABELS[f]}` }}
                      sx={{ p: 0.5 }}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={`Convert the text to ${FORMAT_LABELS[f]} and show it`}>
                  <span>
                    <ButtonBase
                      disabled={disabled}
                      aria-label={`Show as ${FORMAT_LABELS[f]}`}
                      aria-pressed={isShown}
                      onClick={() => switchFormat(f)}
                      sx={{
                        px: 1, py: 0.75, fontSize: 13, borderRadius: 0.5,
                        fontWeight: isShown ? 600 : 400,
                        color: isShown ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {FORMAT_LABELS[f]}
                    </ButtonBase>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>View</Typography>
          <ToggleButtonGroup
            size="small" exclusive value={view} sx={groupSx}
            onChange={(e, next) => next && setView(next)}
          >
            <ToggleButton value="raw"><CodeIcon fontSize="small" sx={{ mr: 0.5 }} />Raw</ToggleButton>
            <ToggleButton value="preview"><VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />Preview</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {view === 'raw' ? (
        <TextField
          size="small" fullWidth multiline minRows={6} maxRows={18}
          value={viewText}
          disabled={disabled}
          error={Boolean(tooLong)}
          onChange={(e) => handleEdit(e.target.value)}
          placeholder={FORMAT_HINTS[format]}
          InputProps={{ sx: format === 'plain' ? undefined : { fontFamily: 'monospace', fontSize: 13 } }}
        />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2, minHeight: 150, maxHeight: 420, overflowY: 'auto',
            bgcolor: 'action.hover', borderStyle: 'dashed',
          }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.2 }}>
            Preview — how this {FORMAT_LABELS[format]} renders · not editable
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {previewBlocks.length > 0
            ? renderBlocks(previewBlocks)
            : <Typography variant="body2" color="text.secondary" fontStyle="italic">Nothing to preview yet.</Typography>}
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
        Tick/check the format of the source text to set how it should be read, this does not
        change the source itself.
        <br />
        Click/select a format to view the converted text as it will be returned by the API.
        Note, the API only returns the RAW text.
      </Typography>

      {isLens && (
        <Alert
          severity="info"
          icon={false}
          sx={{ mt: 1, py: 0.25 }}
          action={!disabled && (
            // describeChild: without it MUI uses the tooltip as the button's
            // accessible name, hiding the visible "Convert & store" label.
            <Tooltip
              describeChild
              title={`Replace the stored description with this ${FORMAT_LABELS[viewFormat]} version. This does change the text.`}
            >
              <Button size="small" color="inherit" onClick={() => convertStoredTo(viewFormat)}>
                Convert &amp; store
              </Button>
            </Tooltip>
          )}
        >
          <Typography variant="caption">
            Viewing as <strong>{FORMAT_LABELS[viewFormat]}</strong> — a preview of what the API
            returns. Still stored as <strong>{FORMAT_LABELS[format]}</strong>. Ticking a box only
            relabels the stored text; use Convert to replace it with this version.
          </Typography>
        </Alert>
      )}

      {/* Editing gives no sight of what was there before — so offer it. */}
      {original && original.text ? (
        <Box sx={{ mt: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              Originally saved as <strong>{FORMAT_LABELS[original.format] || 'Plain text'}</strong>
              {isDirty ? ' · unsaved changes' : ' · unchanged'}
            </Typography>
            <Link component="button" type="button" variant="caption" underline="hover"
              onClick={() => setShowOriginal((s) => !s)}>
              {showOriginal ? 'Hide original' : 'View original'}
            </Link>
            {isDirty && !disabled && (
              <Link component="button" type="button" variant="caption" underline="hover" color="warning.main"
                onClick={revertToOriginal}>
                Revert
              </Link>
            )}
          </Stack>
          {showOriginal && (
            <Paper variant="outlined" sx={{ mt: 0.75, p: 1.5, bgcolor: 'action.hover', maxHeight: 220, overflowY: 'auto' }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Stored {FORMAT_LABELS[original.format] || 'Plain text'} — read only
              </Typography>
              <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {original.text}
              </Box>
            </Paper>
          )}
        </Box>
      ) : null}

      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      {tooLong && (
        <Alert severity="error" sx={{ mt: 1 }}>
          The description is {(value || '').length.toLocaleString()} characters,
          over the {maxLength.toLocaleString()} limit.
        </Alert>
      )}
    </Box>
  );
}
